import { Trans, useTranslation } from "react-i18next";
import { sortBy } from "lodash-es";

import { useMachineStatusList } from "@frontend-common/hooks/use-machine-status-list";
import { useShop } from "@/hooks/use-shop";
import { useCoupon } from "@/hooks/use-coupon";
import { formatCurrency } from "@/lib/utils";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal";
import { Button } from "@/components/ui/button";
import { AudioSlot } from "@/features/audio/kiosk-audio";
import { Strikethrough } from "@/routes/machine-selector-screen.$machine-type";

export type ChosenDryerBundle = { id: number; minutes: number };

/** A dryer's programs (bundles) as cards, each with an info popup. Shared by
 * the dryer bundle screen and the payment flow. */
export function DryerBundleChoice({
  machineId,
  onSelect,
}: {
  machineId: number;
  onSelect: (bundle: ChosenDryerBundle) => void;
}) {
  const { t } = useTranslation();
  const { showModal, closeModal } = useMuiModal();
  const { data: shop } = useShop();
  const coupon = useCoupon();
  const { data: state } = useMachineStatusList(shop.shopId, {
    couponNonceKey: coupon?.couponKey,
  });
  const selectedMachine = state?.find((x: any) => x.machineId === machineId);
  const dryerBundleList = sortBy(
    selectedMachine?.dryerBundles ?? [],
    (elem: any) => elem.view_order,
  );

  return (
    <div className="flex flex-wrap justify-center gap-8 mb-8">
      {dryerBundleList?.map((bundle) => (
        <div
          key={bundle.id}
          className="max-w-72"
          onClick={() => {
            if (bundle.available) {
              void recordTrackedEvent({
                eventType: "dryer_bundle_selected",
                machineId,
                dryerBundleId: bundle.id,
                payload: { programLength: bundle.program_length },
              });
              onSelect({ id: bundle.id, minutes: bundle.program_length });
            }
          }}
        >
          <div
            className={`
              h-full flex flex-col items-center rounded-lg border-2 shadow-lg cursor-pointer
              ${
                bundle.available
                  ? "border-bub-vivid-blue text-bub-darker-blue"
                  : "border-gray-400 text-gray-400"
              }
            `}
          >
            <div className="relative w-full flex justify-center pt-4 items-center">
              <div
                className="dryer-bundle-info-icon absolute right-2 top-2 w-8 h-8 bg-no-repeat bg-contain cursor-pointer"
                style={{
                  backgroundImage: "url('/dryer-bundle-info.svg')",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  showModal(
                    <Modal>
                      <AudioSlot slot="modal.dryer-bundle-info" />
                      <ModalTitle>
                        {t(
                          resolveTranslatonKey(
                            bundle.marketing_name_translation_key,
                          ) + " szárítás",
                        )}
                      </ModalTitle>
                      <ModalContent>
                        {bundle.popup_information_translation_key ===
                        "dryer_bundle_extra_time_program_popup_information" ? (
                          <p className={`leading-6 mb-6`}>
                            <Trans>
                              Amennyiben maradt néhány ruhadarabod, ami még
                              nedves, de nem igényel egy teljes/hosszabb
                              programot, választhatod a 13 perces kiegészítő
                              szárítást, hogy biztosan minden holmid
                              megfelelően megszáradjon!
                            </Trans>
                          </p>
                        ) : (
                          <p>
                            <b>
                              <Trans>
                                Ezekhez a textilekhez ajánljuk:{" "}
                              </Trans>
                            </b>
                            {t(
                              resolveTranslatonKey(
                                bundle.popup_information_translation_key,
                              ),
                            )}
                            <span
                              className={`text-xs leading-6 mt-6 mb-4 block`}
                            >
                              <Trans>
                                A kiválasztott csomag a megadott
                                időhosszúságú szárítást garantálja, a
                                textilek fajtájától és a telítettség
                                mértékétől függően a ruhák szárazságának
                                mértéke esetenként eltérhet.
                              </Trans>
                            </span>
                          </p>
                        )}
                        <Button onClick={closeModal}>
                          <Trans>Rendben</Trans>
                        </Button>
                      </ModalContent>
                    </Modal>,
                  );
                }}
              />
              <div
                className={`
                px-2 py-1 text-center text-2xl font-helvetica flex items-end justify-center
                ${bundle.available ? "text-bub-vivid-blue" : "text-gray-400"}
              `}
              >
                <div
                  className={`
                    w-9 h-9 mr-2 bg-no-repeat bg-contain
                    ${!bundle.available ? "grayscale" : ""}
                  `}
                  style={{
                    backgroundImage: "url('/dryer-bundle-time.svg')",
                  }}
                />
                {bundle.program_length} <Trans>perc</Trans>
              </div>
            </div>
            <div className="text-center mt-4">
              <div
                className={`text-3xl font-bold ${bundle.available ? "text-bub-dark-blue" : "text-gray-400"}`}
              >
                {t(
                  resolveTranslatonKey(
                    bundle.marketing_name_translation_key,
                  ),
                )}
              </div>
            </div>
            <div className="text-center mt-4">
              <div className="px-5 py-4 w-full max-w-max bg-[#61BDCA1F] bg-opacity-10 text-bubbles-dark-blue leading-5 text-xs">
                {t(
                  resolveTranslatonKey(
                    bundle.hint_information_translation_key,
                  ),
                )}
              </div>
            </div>
            <div className="mt-auto p-4 text-center mb-0">
              {bundle.originalPrice &&
              bundle.originalPrice !== bundle.price ? (
                <>
                  <Strikethrough disabled={!bundle.available}>
                    {formatCurrency(
                      bundle.originalPrice,
                      shop?.currencyCode,
                    )}
                  </Strikethrough>
                  <div
                    className={`text-2xl font-helvetica mt-1 ${bundle.available ? "text-bub-dark-blue" : "text-gray-400"}`}
                  >
                    {formatCurrency(bundle.price, shop?.currencyCode)}
                  </div>
                </>
              ) : (
                <div
                  className={`text-2xl font-helvetica ${bundle.available ? "text-bub-dark-blue" : "text-gray-400"}`}
                >
                  {formatCurrency(bundle.price, shop?.currencyCode)}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function resolveTranslatonKey(key: string): string {
  switch (key) {
    case "dryer_bundle_short_program_name":
      // eslint-disable-next-line i18next/no-literal-string
      return "Rövid";

    case "dryer_bundle_normal_program_name":
      // eslint-disable-next-line i18next/no-literal-string
      return "Normál";

    case "dryer_bundle_long_program_name":
      // eslint-disable-next-line i18next/no-literal-string
      return "Hosszú";

    case "dryer_bundle_extra_time_program_name":
      // eslint-disable-next-line i18next/no-literal-string
      return "Kiegészítő";

    case "dryer_bundle_short_program_hint":
      // eslint-disable-next-line i18next/no-literal-string
      return "ideális: dzseki, lepedő, terítő, vékonyabb függöny";

    case "dryer_bundle_normal_program_hint":
      // eslint-disable-next-line i18next/no-literal-string
      return "ideális: póló, pulóver, farmer, törölköző";

    case "dryer_bundle_long_program_hint":
      // eslint-disable-next-line i18next/no-literal-string
      return "ideális: téli kabát, vastag függöny, ágynemű";

    case "dryer_bundle_extra_time_program_hint":
      // eslint-disable-next-line i18next/no-literal-string
      return "Kiegészítő szárítási időt akkor kérhetsz, ha az előző 20 percben indítottál a gépen már egy szárítást";

    case "dryer_bundle_normal_program_hint_non_registered":
      // eslint-disable-next-line i18next/no-literal-string
      return "A választható szárítási időhöz regisztrálj be rendszerünkbe!";

    case "dryer_bundle_short_program_popup_information":
      // eslint-disable-next-line i18next/no-literal-string
      return "dzseki, lepedő, terítő, vékonyabb függöny.";

    case "dryer_bundle_normal_program_popup_information":
      // eslint-disable-next-line i18next/no-literal-string
      return "póló, pulóver, farmer, törölköző.";

    case "dryer_bundle_long_program_popup_information":
      // eslint-disable-next-line i18next/no-literal-string
      return "téli kabát, vastag függöny, ágynemű.";
    default:
      return "";
  }
}
